import { useQueries, useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { APP_REVALIDATE_QUERY_NAMES, PAYMENT_TYPES } from '@/constants';
import { CRYPTO_PROVIDER_PRIORITIES } from '@/hooks/usePaymentMethodsWithNetworks/constant';
import {
  type MethodOption,
  type NetworkOption,
  buildNetworkOptions,
  getPaymentOptions,
} from '@/hooks/usePaymentMethodsWithNetworks/utils';
import { type CryptoAddressResponseType, type PaymentResponseType } from '@/lib/fetch';
import { useTenant } from '@/providers/TenantProvider';
import {
  type PaymentTypes,
  getCryptoPaymentMethodWalletAddresses,
  getPaymentMethods,
} from '@/server-actions/payment-methods';

export interface FullMethod extends MethodOption {
  networkOptions?: NetworkOption[];
  paymentGatewayMethod?: string;
}

export interface UsePaymentMethodsResult {
  isLoading: boolean;
  isError: boolean;
  allMethods: FullMethod[];
  topMethods: FullMethod[];
  otherMethods: FullMethod[];
  creditMethods: FullMethod[];
  allCryptoBases: MethodOption[];
  rawMethods: PaymentResponseType[];
  priorityCryptoList: PaymentResponseType[];
  walletDataByCurrency: CryptoAddressResponseType[];
  providerDetails: Record<
    string,
    {
      method?: PaymentResponseType;
      walletRecords: CryptoAddressResponseType[];
    }
  >;
}

export const usePaymentMethodsWithNetworks = ({
  paymentType,
}: {
  paymentType: PaymentTypes;
}): UsePaymentMethodsResult => {
  const {
    tenantConfig: { settings: { enableVatanCoin = false } = {} },
  } = useTenant();

  const methodsQuery = useQuery<PaymentResponseType[], Error>({
    queryKey: [APP_REVALIDATE_QUERY_NAMES.PAYMENT_METHODS, paymentType],
    select: (data) => {
      if (!enableVatanCoin) {
        return data
          .map((method) => ({
            ...method,
            currencies: method.currencies?.filter((currency) => currency.code !== 'VATAN'),
          }))
          .filter((method) => method.currencies && method.currencies.length > 0);
      }
      return data;
    },
    queryFn: () => getPaymentMethods(paymentType),
  });

  const rawMethods = methodsQuery.data ?? [];

  const { topCryptoOptions, otherCryptoOptions, creditCardOptions } = useMemo(
    () => getPaymentOptions(rawMethods, paymentType === PAYMENT_TYPES.DEPOSITS ? 4 : 5),
    [rawMethods]
  );

  const allCryptoBases = useMemo(
    () => [...topCryptoOptions, ...otherCryptoOptions],
    [topCryptoOptions, otherCryptoOptions]
  );

  const allProviderIds = useMemo(() => {
    const ids = new Set<string>();
    [...allCryptoBases, ...rawMethods.filter((m) => CRYPTO_PROVIDER_PRIORITIES.includes(m.name ?? ''))].forEach(
      (m) => m.providerId && ids.add(m.providerId)
    );
    return Array.from(ids);
  }, [allCryptoBases, rawMethods]);

  const walletQueries = useQueries({
    queries: useMemo(
      () =>
        allProviderIds.map((providerId) => ({
          queryKey: [APP_REVALIDATE_QUERY_NAMES.METHOD_WALLET_ADDRESSES, providerId],
          queryFn: () => getCryptoPaymentMethodWalletAddresses(providerId),
          enabled: methodsQuery.isSuccess,
        })),
      [allProviderIds, methodsQuery.isSuccess]
    ),
  });

  const walletMap = useMemo(() => {
    const map = new Map<string, CryptoAddressResponseType[]>();
    allProviderIds.forEach((id, index) => {
      const data = walletQueries[index].data;
      if (data) map.set(id, data);
    });
    return map;
  }, [walletQueries, allProviderIds]);

  const cryptoFullMethods = useMemo<FullMethod[]>(
    () =>
      allCryptoBases
        .map((base) => {
          const wallets = walletMap.get(base.providerId) ?? [];
          return {
            ...base,
            networkOptions: buildNetworkOptions(wallets, base.currencyCode),
          };
        })
        .filter((m) => (m.networkOptions?.length ?? 0) > 0),
    [allCryptoBases, walletMap]
  );

  const creditFullMethods = useMemo<FullMethod[]>(
    () =>
      creditCardOptions.map((opt) => ({
        ...opt,
        networkOptions: [],
      })),
    [creditCardOptions]
  );

  const topMethods = useMemo(
    () => cryptoFullMethods.slice(0, topCryptoOptions.length),
    [cryptoFullMethods, topCryptoOptions.length]
  );
  const otherMethods = useMemo(
    () => cryptoFullMethods.slice(topCryptoOptions.length),
    [cryptoFullMethods, topCryptoOptions.length]
  );

  // combine and (optionally) drop any credit methods without networkOptions too
  const allMethods = useMemo(
    () => [...cryptoFullMethods, ...creditFullMethods].filter((m) => (m.networkOptions?.length ?? 0) > 0),
    [cryptoFullMethods, creditFullMethods]
  );

  const priorityCryptoList = useMemo(
    () => rawMethods.filter((m) => CRYPTO_PROVIDER_PRIORITIES.includes(m.name ?? '')),
    [rawMethods]
  );

  const walletDataByCurrency = useMemo(() => {
    const currencyMap = new Map<string, CryptoAddressResponseType>();
    walletMap.forEach((records) => {
      records.forEach((record) => {
        if (!record.currency) return;
        const existing = currencyMap.get(record.currency) ?? { currency: record.currency, networks: [] };
        existing?.networks?.push(...(record.networks ?? []));
        currencyMap.set(record.currency, existing);
      });
    });
    return Array.from(currencyMap.values());
  }, [walletMap]);

  const providerDetails = useMemo(() => {
    const details: UsePaymentMethodsResult['providerDetails'] = {};
    CRYPTO_PROVIDER_PRIORITIES.forEach((providerName) => {
      const method = rawMethods.find((m) => m.name === providerName);
      const records = method?.providerId ? walletMap.get(method.providerId) ?? [] : [];
      details[providerName] = { method, walletRecords: records };
    });
    return details;
  }, [rawMethods, walletMap]);

  const isLoading = methodsQuery.isLoading || walletQueries.some((q) => q.isLoading);
  const isError = methodsQuery.isError || walletQueries.some((q) => q.isError);

  return {
    isLoading,
    isError,
    allMethods,
    topMethods,
    otherMethods,
    creditMethods: creditFullMethods,
    allCryptoBases,
    rawMethods,
    priorityCryptoList,
    walletDataByCurrency,
    providerDetails,
  };
};
